import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap',
    'rounded-lg border border-transparent text-[13px] font-medium',
    'transition-[color,background-color,border-color,box-shadow,transform]',
    'duration-[130ms] ease-[cubic-bezier(0.32,0.72,0,1)]',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]',
    'disabled:pointer-events-none disabled:opacity-50',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-[color:var(--primary)] text-[color:var(--primary-foreground)] hover:brightness-95',
        outline:
          'bg-[color:var(--card)] shadow-[var(--shadow-raised)] hover:bg-[color:var(--muted)]',
        secondary:
          'bg-[color:var(--secondary)] text-[color:var(--secondary-foreground)] hover:brightness-95',
        ghost: 'hover:bg-[color:var(--muted)] text-[color:var(--foreground)]',
        destructive:
          'bg-[color:var(--destructive)]/10 text-[color:var(--destructive)] hover:bg-[color:var(--destructive)]/20',
        link: 'text-[color:var(--primary)] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 max-md:min-h-11 px-3',
        sm: 'h-7 max-md:min-h-11 px-2.5 text-[12px]',
        lg: 'h-9 max-md:min-h-11 px-4',
        icon: 'size-8 max-md:size-11',
        'icon-sm': 'size-7 max-md:size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, ...props },
  ref,
) {
  const Component = asChild ? Slot : 'button'
  return (
    <Component ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  )
})

export { buttonVariants }
